import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from '@mui/material';
import { DateTime } from 'luxon';
import { OfflinePeriodAcceptedDateFormat } from 'widgets/OpeningTimes/OfflinePeriods/constants/OfflinePeriodAcceptedDateFormat';
import {
  OfflinePeriodsEvents,
  PeriodGranularity,
} from 'widgets/OpeningTimes/OfflinePeriods/constants/OfflinePeriodsEvents';

import { Button, IconButton, Typography } from '@bolteu/kalep-react';
import { Cross } from '@bolteu/kalep-react-icons';
import { NetworkRequestState } from '@bolteu/redux-session';

import {
  createOfflinePeriodAsyncAction,
  selectOfflinePeriodsCreateError,
  selectOfflinePeriodsCreateRequestState,
} from 'rdx/offlinePeriods';
import { Nullable } from 'types/types';

import { StartEndDatePicker } from 'components/StartEndDatePicker/StartEndDatePicker';
import { useCommonAppServices } from 'root/CommonAppServicesContext';
import { selectSelectedProvider } from 'root/rdx/account';

import { CreateProviderOfflinePeriodRequest } from '../../../../../common/all/api/gen/DeliveryProviderPortalAuthApi';
import { SolidColors } from '../../../../../common/all/styles/bolt-uikit-colors';
import { theme } from '../../../../../common/all/styles/themeMui';

export interface CreateOfflinePeriodDialogProps {
  isOpen: boolean;
  onClose: () => void;
}
const OVERLAPPING_PERIODS_HINT = 'Period overlaps with existing active Offline Periods';
const SCHEDULED_ORDERS_PLACED_HINT = 'Scheduled orders are already placed for the Period';
const SHOULD_START_TOMORROW_HINT = 'Start Date should start at the earliest tomorrow';

export const CreateOfflinePeriodDialog: React.FC<CreateOfflinePeriodDialogProps> = memo(
  ({ isOpen, onClose }) => {
    const { formatMessage } = useIntl();
    const { mixpanelService } = useCommonAppServices();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [isCreationFailed, setIsCreationFailed] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<number>(
      new Date(DateTime.local().plus({ days: 1 }).startOf('day').toMillis()).getTime(),
    );
    const [endDate, setEndDate] = useState<number>(null);
    const startPickerMinMax = useMemo(() => {
      return {
        minDate: new Date(DateTime.local().plus({ days: 1 }).startOf('day').toMillis()).getTime(),
        maxDate: endDate,
      };
    }, [endDate]);
    const endPickerMinMax = useMemo(() => {
      return {
        minDate: startDate,
        maxDate: null,
      };
    }, [startDate]);

    const pickerStrings = useMemo(
      () => ({
        start: formatMessage({ id: 'offlinePeriods.start' }),
        startDescription: formatMessage({
          id: 'offlinePeriods.startDescription',
        }),
        end: formatMessage({ id: 'offlinePeriods.end' }),
      }),
      [formatMessage],
    );
    function useActions() {
      const dispatch = useDispatch();
      return useMemo(
        () => ({
          createOfflinePeriods: (newOfflinePeriod: CreateProviderOfflinePeriodRequest) =>
            dispatch(createOfflinePeriodAsyncAction.request(newOfflinePeriod)),
        }),
        [dispatch],
      );
    }
    const actions = useActions();
    const providerId = useSelector(selectSelectedProvider)?.providerId || null;
    const offlinePeriodsCreateRequestState = useSelector(selectOfflinePeriodsCreateRequestState);
    const offlinePeriodsCreateError = useSelector(selectOfflinePeriodsCreateError);
    const onDurationChanged = useCallback((start: Nullable<number>, end: Nullable<number>) => {
      setStartDate(start);
      setEndDate(end);
    }, []);
    const createOfflinePeriod = useCallback(async () => {
      if (providerId) {
        actions.createOfflinePeriods({
          provider_id: providerId,
          start_date: DateTime.fromMillis(startDate).toFormat(OfflinePeriodAcceptedDateFormat),
          end_date: DateTime.fromMillis(endDate).toFormat(OfflinePeriodAcceptedDateFormat),
        });
      }
    }, [actions, startDate, endDate, providerId]);
    const onCreateOfflinePeriod = useCallback(async () => {
      setIsCreating(true);
      await createOfflinePeriod();
      setIsCreating(false);
    }, [createOfflinePeriod]);
    const onRequestClose = useCallback(() => {
      onClose();
    }, [onClose]);

    useEffect(() => {
      if (offlinePeriodsCreateRequestState === NetworkRequestState.success) {
        mixpanelService.trackProviderEvent(OfflinePeriodsEvents.OFFLINE_PERIOD_ADD_SUBMITTED, {
          providerId,
          period_start: DateTime.fromMillis(startDate).toFormat(OfflinePeriodAcceptedDateFormat),
          period_end: DateTime.fromMillis(endDate).toFormat(OfflinePeriodAcceptedDateFormat),
          is_success: true,
          period_granularity: PeriodGranularity.DATE,
          error_code: null,
        });
        onClose();
      }

      if (
        offlinePeriodsCreateRequestState === NetworkRequestState.failure &&
        startDate &&
        endDate
      ) {
        mixpanelService.trackProviderEvent(OfflinePeriodsEvents.OFFLINE_PERIOD_ADD_SUBMITTED, {
          providerId,
          period_start: DateTime.fromMillis(startDate).toFormat(OfflinePeriodAcceptedDateFormat),
          period_end: DateTime.fromMillis(endDate).toFormat(OfflinePeriodAcceptedDateFormat),
          is_success: false,
          period_granularity: PeriodGranularity.DATE,
          error_code: offlinePeriodsCreateError,
        });
      }
    }, [
      endDate,
      mixpanelService,
      offlinePeriodsCreateError,
      offlinePeriodsCreateRequestState,
      onClose,
      providerId,
      startDate,
    ]);

    useEffect(() => {
      if (offlinePeriodsCreateError) {
        setIsCreationFailed(true);
      }
    }, [offlinePeriodsCreateError, setIsCreationFailed]);

    const errorMessageId = useCallback(() => {
      switch (offlinePeriodsCreateError) {
        case OVERLAPPING_PERIODS_HINT:
          return 'offlinePeriods.errorPeriodOverlaps';
        case SCHEDULED_ORDERS_PLACED_HINT:
          return 'offlinePeriods.errorExistingScheduledOrders';
        case SHOULD_START_TOMORROW_HINT:
          return 'offlinePeriods.errorShouldStartTomorrow';
        default:
          return '';
      }
    }, [offlinePeriodsCreateError]);

    return (
      <Box>
        <Dialog
          fullScreen={isFullScreen}
          maxWidth={false}
          open={isOpen}
          onClose={onRequestClose}
          PaperProps={
            isFullScreen
              ? {}
              : {
                  style: {
                    position: 'absolute',
                    right: '5%',
                    top: '5%',
                    bottom: '5%',
                    margin: 0,
                    height: '100%',
                    maxHeight: '90%',
                  },
                }
          }
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="baseline">
              <Typography variant="title-primary">
                {formatMessage({
                  id: 'offlinePeriods.createOfflinePeriodTitle',
                })}
              </Typography>
              <IconButton aria-label="cancel" size="md" icon={<Cross />} onClick={onRequestClose} />
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box maxWidth={{ md: 515 }}>
              <Alert severity="info">
                <Typography variant="body-primary">
                  {formatMessage({
                    id: 'offlinePeriods.createOfflinePeriodDisclaimer',
                  })}
                </Typography>
              </Alert>
            </Box>
            <Box mt={2} mb={3}>
              <StartEndDatePicker
                strings={pickerStrings}
                startDate={startDate}
                endDate={endDate}
                startPickerProps={startPickerMinMax}
                endPickerProps={endPickerMinMax}
                onChange={onDurationChanged}
              />
            </Box>
            {isCreationFailed && (
              <Box maxWidth={{ md: 515 }}>
                <Alert variant="filled" severity="error">
                  {formatMessage({ id: errorMessageId() })}
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: `1px solid ${SolidColors.neutral300}` }}>
            <Button autoFocus variant="secondary" onClick={onRequestClose}>
              {formatMessage({ id: 'offlinePeriods.cancel' })}
            </Button>
            <Button size="md" onClick={onCreateOfflinePeriod} disabled={isCreating}>
              {formatMessage({ id: 'offlinePeriods.add' })}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  },
);
